/** @jsxImportSource solid-js */

import Modal from "@lutaok/solid-modal";
import { customElement, noShadowDOM } from "solid-element";

import {
  createEffect,
  createSignal,
  type ValidComponent,
  createMemo,
  Show,
} from "solid-js";
import { Dynamic } from "solid-js/web";
import { PrimaryButton } from "#palette/components/button.solid";
import { ErrorCallout } from "#palette/components/callout.solid";
import { createCodeInput } from "#palette/components/form/code.solid";
import { createEmailInput } from "#palette/components/form/email.solid";
import { Spinner } from "#palette/components/spinner.solid";
import { globalState } from "#root/frontend/state.js";

export function RecoveryPINModal() {
  const [currentPage, setCurrentPage] = createSignal(0);
  const [currentToken, setCurrentToken] = createSignal(new Uint8Array());
  const [errorMessage, setErrorMessage] = createSignal("");

  // Pages /////////////////////////////////////////////////////////////////////
  function Page_0_Email(props: { goNext: () => void }) {
    const {
      component: emailInput,
      valid: emailValid,
      value,
    } = createEmailInput({
      label: "Recovery email address",
      placeholder: "personal.email@gmail.com",
    });

    const [loading, setLoading] = createSignal(false);

    async function handle() {
      setLoading(true);
      setErrorMessage("");
      try {
        const givenEmail = value();
        const givenToken =
          await globalState.client.verifyNewRecoveryEmail(givenEmail);
        setCurrentToken(givenToken);
        props.goNext();
      } catch (e) {
        setErrorMessage((e as Error).message);
      } finally {
        setLoading(false);
      }
    }

    return (
      <>
        <h2 class="text-lg font-bold">Update Recovery Email</h2>
        <p class="text-sm">
          Please enter the email you'd like to use for account recovery here. We
          recommend using a different address than your <code>.edu</code> email.
        </p>
        {emailInput}
        <PrimaryButton disabled={!emailValid() || loading()} onClick={handle}>
          Send PIN
        </PrimaryButton>
        {loading() && <Spinner />}
      </>
    );
  }

  function Page_1_PIN(props: { goNext: () => void }) {
    const { component: pinInput, value: pinValue } = createCodeInput({
      length: 6,
    });
    const pinValid = () => pinValue().length === 6;

    const [loading, setLoading] = createSignal(false);

    async function handle() {
      setLoading(true);
      setErrorMessage("");
      try {
        const verifiedEmail =
          await globalState.client.verifyPartnerAffiliation_Step_2_Decrypt(
            currentToken(),
            pinValue(),
          );

        await globalState.client.resetAccountRecovery(
          verifiedEmail,
          globalState.client.userData.phoneNumber,
        );

        // TODO: is there a decent way to detect if this is still a campus email?
        globalState.client.userData.recoveryPersonalEmailNeeded = false;
        await globalState.client.saveUserData();

        props.goNext();
      } catch (e) {
        setErrorMessage((e as Error).message);
      } finally {
        setLoading(false);
      }
    }

    return (
      <>
        <h2 class="text-lg font-semibold">Enter Your PIN</h2>
        <p class="text-sm">
          We've sent a 6-digit PIN to your recovery email. Please enter it
          below.
        </p>
        {pinInput}
        <PrimaryButton disabled={!pinValid() || loading()} onClick={handle}>
          Confirm
        </PrimaryButton>
        {loading() && <Spinner />}
      </>
    );
  }

  function Page_2_Thanks(props: { goNext: () => void }) {
    return (
      <>
        <h2 class="text-lg font-semibold">Recovery email updated</h2>
        <p class="text-sm">
          Your account recovery email has been successfully updated. You can use
          it to recover your account if you ever lose access.
        </p>
        <PrimaryButton onClick={props.goNext}>Done</PrimaryButton>
      </>
    );
  }

  //////////////////////////////////////////////////////////////////////////////

  const pages = [Page_0_Email, Page_1_PIN, Page_2_Thanks];

  createEffect(() => {
    if (currentPage() === pages.length) {
      globalState.setShouldShowRecoveryUpdateModal(false);
    }
  });

  const selectedPage = createMemo(() => {
    const p = pages.at(currentPage());
    return p;
  });

  return (
    <Modal
      isOpen={globalState.shouldShowRecoveryUpdateModal()}
      onCloseRequest={() => {
        globalState.setShouldShowRecoveryUpdateModal(false);
        setCurrentPage(0);
      }}
      closeOnOutsideClick={true}
      overlayClass="bg-brand-900/20 fixed top-0 left-0 grid w-screen h-screen z-50"
      contentClass="relative rounded-lg p-5 flex flex-col gap-2 bg-white border border-brand-500 shadow content-start place-self-center max-w-xl"
    >
      {errorMessage() !== "" && (
        <ErrorCallout title="We couldn't update your recovery email.">
          {errorMessage()}
        </ErrorCallout>
      )}
      <Dynamic
        component={selectedPage()}
        goNext={() => {
          setCurrentPage(currentPage() + 1);
        }}
      />
    </Modal>
  );
}

customElement("recovery-pin-update-modal", () => {
  noShadowDOM();

  return <RecoveryPINModal />;
});
